import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Page, Document, pdfjs } from "react-pdf";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";

import { updateDraft } from "api";
import { ExtractedPackingListTableItem } from "api/models/documentProcessor";
import { useGetExtractedPackingListDetails } from "api/queries/documentProcessor/useGetExtractedPackingListDetails";
import { useGetPackingListConfig } from "api/queries/location/useGetPackingListConfig";
import { updateExtractedPackingListJobStatus } from "api/services/documentProcessor";
import { ReactComponent as CloseIcon } from "assets/images/iconsV2/x-close.svg";
import Button from "components/v2/Button/Button";
import Drawer from "components/v2/Drawer/Drawer";
import errorToast from "helpers/errorToast";
import { useGetPaymentLocationFormDetails } from "api/queries/location/useGetPaymentLocationFormDetails";
import ExtractedPackingListForm from "./ExtractedPackingListForm";
import { getDefaultExtractedPackingListFormValues } from "./ExtractedPackingListForm/helpers";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface ExtractedPackingListReviewProps {
  isOpen: boolean;
  onClose: () => void;
  selectedJob?: ExtractedPackingListTableItem;
}

export default function ExtractedPackingListReview({ isOpen, onClose, selectedJob }: ExtractedPackingListReviewProps) {
  const navigate = useNavigate();
  const methods = useForm();
  const [numPages, setNumPages] = useState<number>();

  function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
    setNumPages(numPages);
  }

  const { data: extractedPackingListDetails } = useGetExtractedPackingListDetails(selectedJob?.ocr_job_id!, {
    enabled: !!selectedJob,
  });
  const isQuote = extractedPackingListDetails?.request_type === "shipment";

  const { data: packingListConfig }: any = useGetPackingListConfig(
    extractedPackingListDetails?.payment_location_reference_number!,
    {
      enabled: !!extractedPackingListDetails?.payment_location_reference_number,
    },
  );
  const defaultHandlingUnit = Object.keys(packingListConfig?.handling_unit_options?.[0] ?? {})?.[0];

  const { data: paymentLocationFormDetails, isLoading: isLoadingPaymentLocationFormDetails } =
    useGetPaymentLocationFormDetails({
      location: extractedPackingListDetails?.payment_location_reference_number || "",
      isQuote,
    });
  const { default_currency } = paymentLocationFormDetails || {};

  useEffect(() => {
    if (!extractedPackingListDetails?.draft_data || !default_currency || !defaultHandlingUnit) return;
    methods.reset(
      getDefaultExtractedPackingListFormValues({ extractedPackingListDetails, default_currency, defaultHandlingUnit }),
    );
  }, [extractedPackingListDetails?.draft_data, default_currency, defaultHandlingUnit, isLoadingPaymentLocationFormDetails]);

  const { file_url } = extractedPackingListDetails || {};
  const draftId = extractedPackingListDetails?.draft_reference_number;

  const { mutate: createDraftRequest, isLoading: isCreatingDraftRequest } = useMutation({
    mutationFn: () => {
      const values = methods.getValues() as any;
      return updateDraft(values, draftId!, !isQuote);
    },
    onSuccess: () => {
      const draftUrl = isQuote ? `/quote-request/?draft_id=${draftId}` : `/quick-request/?draft_id=${draftId}`;
      navigate(draftUrl);
    },
    onError: (error: any) => {
      errorToast(error);
    },
  });

  const { mutate: updateStatus, isLoading: isUpdatingStatus } = useMutation({
    mutationFn: () => updateExtractedPackingListJobStatus(selectedJob?.ocr_job_id!, { status: "pending_conversion" }),
    onSuccess: () => {
      createDraftRequest();
    },
    onError: (error: any) => {
      errorToast(error);
    },
  });

  const handleClickCreateDraftRequest = () => {
    if (!draftId) {
      errorToast("Draft reference number is missing");
      return;
    }
    updateStatus();
  };

  return (
    <Drawer size="full" isOpen={isOpen} onClose={onClose}>
      <div className="d-flex">
        {/* Display PDF file */}
        <div style={{ width: "50%", height: "100vh", overflowY: "auto", position: "fixed" }} className="p-xl">
          <div style={{ overflow: "auto" }} className="border border-secondary p-md">
            <Document file={file_url} onLoadSuccess={onDocumentLoadSuccess}>
              {Array.from(new Array(numPages), (_, index) => (
                <Page key={index} pageNumber={index + 1} renderTextLayer={false} renderAnnotationLayer={false} />
              ))}
            </Document>
          </div>
        </div>

        <div style={{ width: "50%", marginLeft: "50%" }} className="bg-secondary">
          <div className="px-3xl py-3xl">
            <div className="d-flex justify-content-between pb-3xl">
              <div>
                <div className="display-xs font-bold">{selectedJob?.file_name}</div>
                <div className="text-md text-secondary">
                  <FormattedMessage
                    id="documentParser.uploadPage.reviewDrawer.subtitle"
                    defaultMessage="Please ensure all of the information are correct"
                  />
                </div>
              </div>
              <CloseIcon className="cursor-pointer" onClick={onClose} />
            </div>

            <FormProvider {...methods}>
              <ExtractedPackingListForm
                extractedPackingListDetails={extractedPackingListDetails}
                selectedJob={selectedJob}
              />
            </FormProvider>
          </div>

          <div className="document-parser-review-drawer-footer">
            <Button
              variant="primary"
              onClick={handleClickCreateDraftRequest}
              label={
                <FormattedMessage
                  id="documentParser.uploadPage.reviewDrawer.createDraftRequest"
                  defaultMessage="Create draft request"
                />
              }
              className="text-capitalize"
              disabled={isCreatingDraftRequest || isUpdatingStatus}
            />
          </div>
        </div>
      </div>
    </Drawer>
  );
}

ExtractedPackingListReview.defaultProps = {
  selectedJob: null,
};
